import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { T, useT } from "@transifex/react";
import { zodResolver } from "storybook/utils/form";
import { NAVIGATOR_HOSTNAME, SPECIALIST_HOSTNAME } from "env-vars";
import { usePatient } from "context/advert/use-patient";
import { AvailabilityPicker } from "components/availability-picker";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  FormSection,
  SectionTitle,
  InputGroup,
  InputWrapper,
  Label,
  Input,
  ModalFooter,
  CancelButton,
  SubmitButton,
  ErrorMessage,
  SuccessMessage,
  SuccessIcon,
  SuccessTitle,
  SuccessText,
} from "./styles";
import {
  availabilitySchema,
  type AvailabilityFormType,
  type DayAvailabilityType,
} from "./availability-schema";

/**
 * Maps a riskhub item's `interest` string to the specialist PersonStatus
 * `risk_guideline` enum integer.
 *
 * Specialist enum (from person_status.rb):
 *   breast: 0, prostate: 1, cervical: 2, colon: 3,
 *   lung: 4, mri: 5, counseling: 6, testing: 7
 */
const INTEREST_TO_RISK_GUIDELINE: Record<string, number> = {
  testing: 7,
  genetic_testing: 7,
  mri: 5,
  breast_mri: 5,
  lung: 4,
  lung_ct: 4,
  counseling: 6,
  genetic_counseling: 6,
};

/** Risk guidelines that historically route to the navigator service */
const NAVIGATOR_GUIDELINES = new Set([7]); // testing

function normalizeInterest(interest: string | undefined): string {
  return String(interest ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function resolveRiskGuideline(interest: string | undefined): number | null {
  const key = normalizeInterest(interest);
  return INTEREST_TO_RISK_GUIDELINE[key] ?? null;
}

// ── Payload builder ──────────────────────────────────────────────────

interface SelectedCard {
  /** The riskhub item's `interest` attribute value (e.g. "testing", "mri") */
  interest: string;
}

function getDayAvailability(day: DayAvailabilityType): ("AM" | "PM")[] | undefined {
  const slots: ("AM" | "PM")[] = [];
  if (day.enabled && day.am) slots.push("AM");
  if (day.enabled && day.pm) slots.push("PM");
  return slots.length > 0 ? slots : undefined;
}

function buildAvailabilityPayload(form: AvailabilityFormType) {
  return {
    start_time: Date.now(),
    stop_time: Date.now() + 360000,
    availability: {
      anytime: !!form.anytime,
      monday: getDayAvailability(form.monday),
      tuesday: getDayAvailability(form.tuesday),
      wednesday: getDayAvailability(form.wednesday),
      thursday: getDayAvailability(form.thursday),
      friday: getDayAvailability(form.friday),
    },
  };
}

const IN_PERSON_INTERESTED_STATUS = 21;
const PATIENT_PORTAL_METHOD = 4;

function buildPersonPayload(
  guidelines: number[],
  email: string,
  phone: string,
  availability: ReturnType<typeof buildAvailabilityPayload>
) {
  const currentTime = Math.floor(Date.now());
  return {
    person: {
      ...(email ? { email } : {}),
      ...(phone ? { phone_number: phone } : {}),
      replace_appointments: true,
      person_statuses_attributes: guidelines.map((rg) => ({
        status: IN_PERSON_INTERESTED_STATUS,
        risk_guideline: rg,
      })),
      preferred_appointment_times_attributes: [availability],
      person_contact_histories_attributes: guidelines.map((rg) => ({
        actionDateTime: currentTime,
        in_person_reason: 1,
        user_email: email,
        risk_guideline: rg,
        method: PATIENT_PORTAL_METHOD,
      })),
    },
  };
}

// ── Component ────────────────────────────────────────────────────────

export interface SimpleRequestCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The riskhub cards the user selected for follow-up */
  selectedCards: SelectedCard[];
  /**
   * When true all guidelines go to the specialist service (navigator is not used).
   * Matches the `removeAccessToNavigatorApp` feature flag.
   */
  specialistOnly?: boolean;
  /** Custom modal title. Falls back to "Request a Call" when empty/undefined. */
  title?: string;
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
      fill="currentColor"
    />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
      fill="currentColor"
    />
  </svg>
);

const defaultDay = { enabled: false, am: false, pm: false };

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

export function SimpleRequestCallModal({
  isOpen,
  onClose,
  selectedCards,
  specialistOnly = false,
  title,
}: SimpleRequestCallModalProps) {
  const t = useT();
  const displayTitle = title?.trim() || t("Request a Call");
  const { patient } = usePatient();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<AvailabilityFormType>({
    resolver: zodResolver(availabilitySchema),
    defaultValues: {
      anytime: false,
      monday: defaultDay,
      tuesday: defaultDay,
      wednesday: defaultDay,
      thursday: defaultDay,
      friday: defaultDay,
    },
  });

  const { handleSubmit } = form;

  // Prefill email from localStorage and phone from patient data
  useEffect(() => {
    if (!isOpen) return;

    if (!email) {
      const storedEmail = localStorage.getItem("ciq_email");
      if (storedEmail) setEmail(storedEmail);
    }

    if (!phone && patient?.phone_number) {
      setPhone(formatPhone(patient.phone_number));
    }
  }, [isOpen, patient]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsSuccess(false);
      setEmail("");
      setPhone("");
      form.reset();
    }
  }, [isOpen, form]);

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPhone(formatPhone(e.target.value));
    },
    []
  );

  const handleFormSubmit = useCallback(
    async (data: AvailabilityFormType) => {
      setError(null);
      setIsSubmitting(true);

      try {
        const patientId = localStorage.getItem("ciq_id") || "";
        const idToken = localStorage.getItem("id_token");
        const submittedEmail = email.trim();
        const submittedPhone = phone.replace(/\D/g, "");
        const availability = buildAvailabilityPayload(data);

        // Partition guidelines into specialist vs navigator
        const specialistGuidelines: number[] = [];
        const navigatorGuidelines: number[] = [];

        selectedCards.forEach((card) => {
          const rg = resolveRiskGuideline(card.interest);
          if (rg === null) return;

          if (!specialistOnly && NAVIGATOR_GUIDELINES.has(rg)) {
            navigatorGuidelines.push(rg);
          } else {
            specialistGuidelines.push(rg);
          }
        });

        const fetchPromises: Promise<Response>[] = [];

        if (specialistGuidelines.length > 0) {
          fetchPromises.push(
            fetch(`${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify(
                buildPersonPayload(specialistGuidelines, submittedEmail, submittedPhone, availability)
              ),
            })
          );
        }

        if (navigatorGuidelines.length > 0) {
          const navPayload = {
            person: {
              ...(submittedEmail ? { email: submittedEmail } : {}),
              ...(submittedPhone ? { phone_number: submittedPhone } : {}),
              preferred_appointment_times_attributes: [availability],
              person_statuses_attributes: navigatorGuidelines.map((rg) => ({
                status: IN_PERSON_INTERESTED_STATUS,
                risk_guideline: rg,
              })),
              person_contact_histories_attributes: navigatorGuidelines.map(
                (rg) => ({
                  actionDateTime: Math.floor(Date.now()),
                  actionType: 17,
                  in_person_reason: 1,
                  user_email: submittedEmail,
                  risk_guideline: rg,
                })
              ),
              replace_appointments: true,
            },
          };

          fetchPromises.push(
            fetch(`${NAVIGATOR_HOSTNAME}/api/v1/people/${patientId}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify(navPayload),
            })
          );
        }

        if (fetchPromises.length === 0) {
          setIsSuccess(true);
          return;
        }

        const responses = await Promise.all(fetchPromises);

        if (responses.some((r) => !r.ok)) {
          setError(t("Unable to submit your request. Please try again."));
          return;
        }

        setIsSuccess(true);
      } catch (err) {
        console.error("Error submitting availability:", err);
        setError(t("Unable to submit your request. Please try again."));
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedCards, specialistOnly, t]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  // Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Lock scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (isSuccess) {
    return (
      <ModalOverlay onClick={handleOverlayClick} role="dialog" aria-modal="true">
        <ModalContainer>
          <CloseButton
            onClick={onClose}
            aria-label={t("Close")}
            data-testid="SimpleRequestCallModal__CloseButton"
          >
            <CloseIcon />
          </CloseButton>
          <SuccessMessage data-testid="SimpleRequestCallModal__Success">
            <SuccessIcon>
              <CheckIcon />
            </SuccessIcon>
            <SuccessTitle>
              <T _str="Request Submitted" />
            </SuccessTitle>
            <SuccessText>
              <T _str="A representative will contact you during your selected availability. Thank you!" />
            </SuccessText>
          </SuccessMessage>
          <ModalFooter>
            <SubmitButton onClick={onClose}>
              <T _str="Done" />
            </SubmitButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="simple-request-call-title"
      data-testid="SimpleRequestCallModal"
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle id="simple-request-call-title">
            {displayTitle}
          </ModalTitle>
          <ModalSubtitle>
            <T _str="Please indicate the best times for your care team to get in touch to help you schedule your appointment(s)." />
          </ModalSubtitle>
          <CloseButton
            onClick={onClose}
            aria-label={t("Close")}
            data-testid="SimpleRequestCallModal__CloseButton"
          >
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormProvider {...form}>
            <form
              aria-label="availability-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(handleFormSubmit)();
              }}
              data-testid="SimpleRequestCallModal__Form"
            >
              <FormSection>
                <SectionTitle>
                  <T _str="Contact Information" />
                </SectionTitle>
                <InputGroup>
                  <InputWrapper>
                    <Label htmlFor="simple-modal-email">
                      <T _str="Email" />
                    </Label>
                    <Input
                      id="simple-modal-email"
                      type="email"
                      placeholder={t("your@email.com")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="SimpleRequestCallModal__EmailInput"
                    />
                  </InputWrapper>
                  <InputWrapper>
                    <Label htmlFor="simple-modal-phone">
                      <T _str="Phone" />
                    </Label>
                    <Input
                      id="simple-modal-phone"
                      type="tel"
                      placeholder={t("(555) 555-5555")}
                      value={phone}
                      onChange={handlePhoneChange}
                      data-testid="SimpleRequestCallModal__PhoneInput"
                    />
                  </InputWrapper>
                </InputGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <T _str="Preferred Availability" />
                </SectionTitle>
                <AvailabilityPicker
                  namePrefix=""
                  theme="patient-facing"
                  noContainer
                />
              </FormSection>

              {error && (
                <ErrorMessage data-testid="SimpleRequestCallModal__Error">
                  {error}
                </ErrorMessage>
              )}

              <ModalFooter>
                <CancelButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  data-testid="SimpleRequestCallModal__CancelButton"
                >
                  <T _str="Cancel" />
                </CancelButton>
                <SubmitButton
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="SimpleRequestCallModal__SubmitButton"
                >
                  {isSubmitting ? t("Submitting...") : t("Submit")}
                </SubmitButton>
              </ModalFooter>
            </form>
          </FormProvider>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
}
